import { DateTime } from 'luxon'
import {
  DAY_CHECKIN_HOUR,
  DateTimeFormats,
  MAX_HOURS,
  MAX_NIGHTS,
  MIN_HOURS,
  OVERNIGHT_END_HOUR,
  OVERNIGHT_START_HOUR,
} from '../common/constants'
import { PriceOptions } from '../types/bookingFormTypes'

/**
 * Booking inputs are hotel-local wall-clock values. Do not include the
 * visitor's browser offset, because the API interprets these in hotel time.
 */
export const formatHotelDateTime = (date: DateTime): string => date.toFormat(DateTimeFormats.HOTEL_DATE_TIME)

export const getDisabledTimes = (date: Date | null | undefined, minDate: Date | null | undefined) => {
  if (!date || DateTime.fromJSDate(date).hasSame(DateTime.now(), 'day')) {
    const result = []
    const dateObj = minDate ? DateTime.fromJSDate(minDate) : DateTime.now()
    for (let hour = 0; hour <= dateObj.get('hour'); hour++) {
      result.push(dateObj.startOf('day').plus({ hour }).toJSDate())

      if (hour < dateObj.get('hour') || dateObj.get('minute') > 30) {
        result.push(dateObj.startOf('day').plus({ hour, minute: 30 }).toJSDate())
      }
    }

    return result
  }

  return undefined
}

interface IQueryOptions {
  priceOption: PriceOptions
  nights?: any
  hours?: any
  overnightTimeFrom?: string
}

const getHour = (time: string, defaultHour: number) => {
  if (!time) {
    return defaultHour
  }

  const parsed = DateTime.fromFormat(time, 'HH:mm')
  return parsed.isValid ? parsed.hour : defaultHour
}

export const getCheckoutDate = (
  checkInTime: DateTime,
  { priceOption, nights, hours, overnightTimeFrom }: IQueryOptions,
) => {
  const checkOutDate = checkInTime
  const overnightStartHour = getHour(overnightTimeFrom, OVERNIGHT_START_HOUR)
  switch (priceOption) {
    case PriceOptions.Hour:
      return checkOutDate.plus({ hours })
    case PriceOptions.Night:
      if (checkInTime.get('hour') >= overnightStartHour) {
        return checkOutDate.startOf('day').plus({ days: 1, hours: 11 })
      }
      return checkOutDate.startOf('day').plus({ hours: 11 })
    case PriceOptions.Day:
      return checkOutDate.startOf('day').plus({ days: nights, hours: 11 })
    default:
      throw new Error(`Unsupported price option value ${priceOption}`)
  }
}

const DEFAULT_STEP = 0.5
export const getTimeOptions = (startHour: number, endHour: number, step: number = DEFAULT_STEP) => {
  const result = []
  for (let ii = startHour; ii <= endHour; ii += step) {
    result.push({
      value: ii,
      label: DateTime.now()
        .set({
          hour: ii,
          minute: (ii - Math.floor(ii)) * 60,
        })
        .toFormat('HH:mm'),
    })
  }

  return result
}

export const OVERNIGHT_CHECKIN_START = '22:00'
export const OVERNIGHT_CHECKIN_END = '08:00'

export const filterOvernightTime = (time: Date) => {
  const startTime = DateTime.fromFormat(OVERNIGHT_CHECKIN_START, 'HH:mm')
  const endTime = DateTime.fromFormat(OVERNIGHT_CHECKIN_END, 'HH:mm')
  const dt = DateTime.fromJSDate(time)

  return (
    dt.startOf('day').set({ hour: startTime.hour, minute: startTime.minute }) <= dt ||
    dt.startOf('day').set({ hour: endTime.hour, minute: endTime.minute }) >= dt
  )
}

interface IOvernightSettings {
  overnightTimeFrom?: string
  overnightTimeDefault?: string
}

export const getDefaultDate = (
  priceOption: PriceOptions,
  date?: DateTime,
  { overnightTimeFrom, overnightTimeDefault }: IOvernightSettings = {},
): DateTime => {
  const overnightStartHour = getHour(overnightTimeFrom, OVERNIGHT_START_HOUR)
  const currentDate: DateTime =
    priceOption === PriceOptions.Night && overnightTimeDefault && !date
      ? DateTime.now()
          .startOf('day')
          .set({ hour: getHour(overnightTimeDefault, overnightStartHour) })
      : date || DateTime.now()
  let result = currentDate.startOf('minute')
  let minDate: DateTime = DateTime.now().startOf('minute').plus({ minute: 1 })

  if (priceOption === PriceOptions.Night) {
    const firstCheckInDate = currentDate.startOf('day').set({ hour: overnightStartHour })
    const lastCheckInDate = currentDate.startOf('day').set({ hour: OVERNIGHT_END_HOUR })
    minDate = currentDate.startOf('day').set({ hour: overnightStartHour })

    if (currentDate > firstCheckInDate) {
      minDate = currentDate
    } else if (currentDate < lastCheckInDate) {
      minDate = currentDate
    } else {
      minDate = firstCheckInDate
    }
  } else if (priceOption === PriceOptions.Day) {
    minDate = currentDate.startOf('day').set({ hour: DAY_CHECKIN_HOUR })

    if (currentDate > minDate) {
      minDate = currentDate
    }
  } else {
    minDate = DateTime.max(minDate, currentDate)
  }

  if (minDate && result < minDate) {
    result = minDate
  }

  return result
}

export const hoursOption = new Array(MAX_HOURS - MIN_HOURS + 1).fill(0).map((_, idx) => ({
  value: idx + MIN_HOURS,
  label: `${idx + MIN_HOURS} giờ`,
}))

export const nightsOption = new Array(MAX_NIGHTS).fill(0).map((_, idx) => ({
  value: idx + 1,
  label: `${idx + 1} đêm`,
}))
